/**
 * CR-7261: carry the clicked chart figure into the drill-down header.
 *
 * Hover January on the OTD chart and the tooltip says 91.95%; drill in and
 * that number used to vanish — the header carried a context LABEL ("January
 * 2026") and never a VALUE, so the headline could not be reconciled against
 * the records beneath it.
 *
 * OTIF and OTD are the SAME shape — each point carries its percentage plus an
 * optional ``target`` line — so they share one builder rather than two that
 * can drift on rounding or on how a missing target reads. The OTIF half was
 * the one still outstanding: the modal declared and rendered the prop, and no
 * call site ever passed it.
 *
 * The number is formatted here with the same rule the chart tooltip uses, so
 * the header and the tooltip can never disagree about rounding: a header
 * reading 92.0% next to a tooltip reading 91.95% would be a worse bug than
 * the one being fixed.
 */
import type { DrilldownHeadline } from '../../contexts/DrilldownContext';
import { formatKpiPercent } from '../../lib/kpiPercent';

/**
 * The one percent format the dashboard's chart tooltips use.
 *
 * CR-6155: follows the company's KPI decimal setting, like the rings — the
 * tooltip used to show 2 decimals whatever the setting said.
 *
 * @param value - the unrounded percentage
 * @param decimals - `preferences.kpi_percent_decimals`; 2 when not given
 * @returns e.g. "91.95%"
 */
export function formatChartPercent(value: number, decimals = 2): string {
  return formatKpiPercent(value, decimals, '-');
}

/** Headline label shown before the figure in the drill-down header. */
export const CHART_HEADLINE_LABEL = 'chart';

/**
 * The headline for a clicked month, from the point's own values.
 *
 * @param point - the Recharts payload of the clicked month
 * @param metricKey - the point's percentage key (`otd` or `otif`)
 * @returns the headline, or undefined when the point carries no figure
 */
function headlineFromPoint(
  point: Record<string, unknown> | null | undefined,
  metricKey: string,
  decimals: number,
): DrilldownHeadline | undefined {
  if (!point || point[metricKey] == null || point[metricKey] === '') return undefined;
  const pct = Number(point[metricKey]);
  if (!Number.isFinite(pct)) return undefined;
  const hasTarget = point['target'] != null && point['target'] !== '';
  const target = hasTarget ? Number(point['target']) : NaN;
  const value = Number.isFinite(target)
    ? `${formatChartPercent(pct, decimals)} (target ${formatChartPercent(target, decimals)})`
    : formatChartPercent(pct, decimals);
  return { label: CHART_HEADLINE_LABEL, value };
}

/**
 * The OTD headline for a clicked month, from the point's own values.
 *
 * @param point - the Recharts payload of the clicked month (`otd`, optional `target`)
 * @param decimals - `preferences.kpi_percent_decimals`, so the header reads like the tooltip
 * @returns the headline, or undefined when the point carries no OTD figure
 */
export function otdHeadlineFromPoint(
  point: Record<string, unknown> | null | undefined,
  decimals = 2,
): DrilldownHeadline | undefined {
  return headlineFromPoint(point, 'otd', decimals);
}

/**
 * The OTIF headline for a clicked month, from the point's own values.
 *
 * The OTIF chart's point carries `otif` alongside the same `target` line the
 * OTD chart draws (both are `<Line dataKey="target">` on the same axis), so
 * the two headlines read identically and round identically.
 *
 * @param point - the Recharts payload of the clicked month (`otif`, optional `target`)
 * @param decimals - `preferences.kpi_percent_decimals`, so the header reads like the tooltip
 * @returns the headline, or undefined when the point carries no OTIF figure
 */
export function otifHeadlineFromPoint(
  point: Record<string, unknown> | null | undefined,
  decimals = 2,
): DrilldownHeadline | undefined {
  return headlineFromPoint(point, 'otif', decimals);
}
